import { MapPin, Phone, Mail, Navigation, ArrowUpRight } from "lucide-react";
import { FadeUp, SectionHeader } from "./Reveal.jsx";
import { WHATSAPP_URL } from "./WhatsAppFab.jsx";
import { useSiteContent } from "@/content/ContentContext.jsx";

const LocationContact = () => {
  const { content } = useSiteContent();
  const c = content.location;
  const mapEmbed = `https://maps.google.com/maps?q=${encodeURIComponent(c.mapQuery)}&z=15&hl=en&output=embed`;

  return (
    <section id="location" className="relative bg-brand-tint/50 py-20 sm:py-28 lg:py-36" data-testid="location-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-10">
        <SectionHeader eyebrow={c.eyebrow} title={c.title} testid="location-eyebrow" />

        <div className="mt-14 grid gap-8 lg:grid-cols-2">
          <FadeUp>
            <div className="overflow-hidden rounded-3xl border hairline shadow-[0_20px_60px_rgba(7,58,30,0.08)]" data-testid="map-wrapper">
              <iframe
                title="Angukumaran Farmhouse location map"
                src={mapEmbed}
                className="h-[360px] w-full sm:h-[460px]"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="map-embed"
              />
            </div>
            <a
              href={c.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2 rounded-full border border-brand/25 px-6 py-3 text-[13px] font-semibold uppercase tracking-[0.14em] text-brand transition-all duration-300 hover:bg-brand hover:text-white"
              data-testid="directions-button"
            >
              <Navigation size={16} /> Get Directions
            </a>
          </FadeUp>

          <div className="space-y-5">
            <FadeUp delay={0.06}>
              <div className="flex items-start gap-5 rounded-3xl border hairline bg-white p-7" data-testid="contact-address-card">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-tint2 text-brand">
                  <MapPin size={22} />
                </span>
                <div>
                  <h3 className="font-display text-lg text-brand">Address</h3>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-600 whitespace-pre-line" data-testid="contact-address-text">
                    {c.address}
                  </p>
                </div>
              </div>
            </FadeUp>

            <FadeUp delay={0.12}>
              <div className="flex items-start gap-5 rounded-3xl border hairline bg-white p-7" data-testid="contact-phone-card">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-tint2 text-brand">
                  <Phone size={22} />
                </span>
                <div>
                  <h3 className="font-display text-lg text-brand">Call / WhatsApp</h3>
                  <a href={`tel:${c.phone.replace(/\s/g, "")}`} className="mt-2 block text-sm font-semibold text-ink transition-colors hover:text-brand" data-testid="contact-phone-link">
                    {c.phone}
                  </a>
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1.5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1da851] transition-colors hover:text-[#25D366]"
                    data-testid="contact-whatsapp-link"
                  >
                    Chat on WhatsApp
                    <ArrowUpRight size={14} />
                  </a>
                </div>
              </div>
            </FadeUp>

            <FadeUp delay={0.18}>
              <div className="flex items-start gap-5 rounded-3xl border hairline bg-white p-7" data-testid="contact-email-card">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-tint2 text-brand">
                  <Mail size={22} />
                </span>
                <div>
                  <h3 className="font-display text-lg text-brand">Email</h3>
                  <a href={`mailto:${c.email1}`} className="mt-2 block text-sm font-semibold text-ink transition-colors hover:text-brand" data-testid="contact-email-link-1">
                    {c.email1}
                  </a>
                  <a href={`mailto:${c.email2}`} className="mt-1 block text-sm font-semibold text-ink transition-colors hover:text-brand" data-testid="contact-email-link-2">
                    {c.email2}
                  </a>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LocationContact;
